// Blaivoré — floating 3D shapes in the hero background (Three.js r128).
// Scoped to the homepage hero only, paused off-screen, and skipped
// entirely on touch/narrow/reduced-motion so it can never cost mobile
// visitors battery or make the page feel slow. If anything here throws
// or WebGL isn't available, the hero just renders exactly as before —
// this is a pure visual add-on with no effect on layout or content.

(function () {
  "use strict";

  function safe(label, fn) {
    try {
      fn();
    } catch (err) {
      if (window.console && console.warn) {
        console.warn("[blaivore-hero-3d] " + label + " failed:", err);
      }
    }
  }

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
  var isNarrow = window.innerWidth < 700;

  if (reduceMotion || isTouch || isNarrow) return;

  safe("hero 3d scene", function () {
    if (!window.THREE) return;
    var mount = document.querySelector(".hero .hero__media-grid");
    if (!mount) return;

    var width = mount.clientWidth || window.innerWidth;
    var height = mount.clientHeight || window.innerHeight;
    if (!width || !height) return;

    var canvas = document.createElement("canvas");
    canvas.className = "bx-hero-canvas";

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true });
    } catch (e) {
      return; // WebGL unavailable — leave the hero exactly as it was
    }
    if (!renderer) return;

    mount.appendChild(canvas);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.z = 18;

    // High-contrast key/rim lights (vivid orange + white) — recreates the
    // Tonic preset's bold sporty look, swapped from the earlier warm pass.
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    var key = new THREE.DirectionalLight(0xffffff, 1.3);
    key.position.set(5, 6, 8);
    scene.add(key);
    var rim = new THREE.DirectionalLight(0xffffff, 0.65);
    rim.position.set(-6, -3, 4);
    scene.add(rim);

    // Sharper, more technical shape mix — polyhedra lead, one torus for
    // variety — reading as sporty/precise rather than soft.
    var geometries = [
      new THREE.IcosahedronGeometry(1, 0),
      new THREE.OctahedronGeometry(1, 0),
      new THREE.TorusGeometry(0.8, 0.24, 10, 24),
      new THREE.TetrahedronGeometry(1.1, 0)
    ];
    // No pure-black entries here — the hero backdrop is already
    // near-black, so a black shape (wireframe especially) would vanish.
    var palette = [0x000000, 0x1a1a1a, 0x555555, 0x000000];

    var shapes = [];
    var COUNT = 11;
    for (var i = 0; i < COUNT; i++) {
      var geo = geometries[i % geometries.length];
      var isWire = i % 3 === 0;
      var mat = isWire
        ? new THREE.MeshBasicMaterial({
            color: palette[i % palette.length],
            wireframe: true,
            transparent: true,
            opacity: 0.55
          })
        : new THREE.MeshStandardMaterial({
            color: palette[i % palette.length],
            roughness: 0.35,
            metalness: 0.2,
            transparent: true,
            opacity: 0.88
          });
      var mesh = new THREE.Mesh(geo, mat);
      var scale = 0.4 + Math.random() * 0.85;
      mesh.scale.setScalar(scale);
      // Bias shapes toward the edges/corners so they never sit directly
      // over the centered headline and CTA — atmosphere, not clutter.
      var sideX = Math.random() < 0.5 ? -1 : 1;
      var sideY = Math.random() < 0.5 ? -1 : 1;
      mesh.position.set(
        sideX * (3.2 + Math.random() * 5.8),
        sideY * (1.8 + Math.random() * 3.4),
        (Math.random() - 0.5) * 8 - 2
      );
      mesh.userData.spin = { x: (Math.random() - 0.5) * 0.01, y: (Math.random() - 0.5) * 0.012 };
      mesh.userData.bob = {
        speed: 0.4 + Math.random() * 0.6,
        offset: Math.random() * Math.PI * 2,
        amp: 0.3 + Math.random() * 0.5
      };
      mesh.userData.baseY = mesh.position.y;
      scene.add(mesh);
      shapes.push(mesh);
    }

    var targetRotX = 0, targetRotY = 0, curRotX = 0, curRotY = 0;
    function setTarget(clientX, clientY) {
      var w = window.innerWidth, h = window.innerHeight;
      // Same guard as the rest of the site's motion code: a zero/invalid
      // viewport reading must never be allowed to poison the eased value.
      if (!w || !h) return;
      var nx = (clientX / w) * 2 - 1;
      var ny = (clientY / h) * 2 - 1;
      if (!isFinite(nx) || !isFinite(ny)) return;
      targetRotY = nx * 0.25;
      targetRotX = ny * 0.15;
    }
    window.addEventListener("mousemove", function (e) { setTarget(e.clientX, e.clientY); });

    var running = true;
    var clock = new THREE.Clock();

    function animate() {
      if (!running) return;
      requestAnimationFrame(animate);
      var t = clock.getElapsedTime();

      curRotX += (targetRotX - curRotX) * 0.04;
      curRotY += (targetRotY - curRotY) * 0.04;
      if (!isFinite(curRotX)) curRotX = 0;
      if (!isFinite(curRotY)) curRotY = 0;
      scene.rotation.x = curRotX;
      scene.rotation.y = curRotY;

      shapes.forEach(function (mesh) {
        mesh.rotation.x += mesh.userData.spin.x;
        mesh.rotation.y += mesh.userData.spin.y;
        var b = mesh.userData.bob;
        mesh.position.y = mesh.userData.baseY + Math.sin(t * b.speed + b.offset) * b.amp;
      });

      renderer.render(scene, camera);
    }
    requestAnimationFrame(animate);

    // Pause the whole render loop once the hero scrolls out of view, and
    // resume it if the visitor scrolls back — keeps this from burning
    // battery/GPU on the rest of the site.
    var heroSection = document.querySelector(".hero");
    if ("IntersectionObserver" in window && heroSection) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var wasRunning = running;
          running = entry.isIntersecting;
          if (running && !wasRunning) requestAnimationFrame(animate);
        });
      }, { threshold: 0.02 });
      io.observe(heroSection);
    }

    function onResize() {
      var w = mount.clientWidth || window.innerWidth;
      var h = mount.clientHeight || window.innerHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    window.addEventListener("resize", onResize);
  });
})();
